import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Divider,
  IconButton
} from '@mui/material';
import {
  Assignment,
  Favorite,
  AccountCircle,
  Warning,
  Notifications,
  Event,
  Help,
  Settings,
  Close,
  Campaign,
  PointOfSale
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ReviewerDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ReviewerDrawer: React.FC<ReviewerDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const handleMenuClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const menuItems = [
    {
      title: '활동관리',
      items: [
        {
          label: '캠페인 신청내역',
          icon: <Assignment />,
          path: '/mypage/reviewer/campaign-applications'
        },
        {
          label: '찜한 캠페인',
          icon: <Favorite />,
          path: '/mypage/reviewer/liked-campaigns'
        }
      ]
    },
    {
      title: '계정관리',
      items: [
        {
          label: '내 계정',
          icon: <AccountCircle />,
          path: '/mypage/reviewer/edit-profile'
        },
        {
          label: '페널티 관리',
          icon: <Warning />,
          path: '/mypage/reviewer/penalty'
        },
        {
          label: '내 포인트',
          icon: <PointOfSale />,
          path: '/mypage/reviewer/points'
        }
      ]
    },
    {
      title: '고객 센터',
      items: [
        {
          label: '공지사항',
          icon: <Notifications />,
          path: '/mypage/notice'
        },
        {
          label: '이벤트',
          icon: <Event />,
          path: '/mypage/events'
        },
        {
          label: '1:1문의',
          icon: <Help />,
          path: '/mypage/inquiry'
        },
        {
          label: '광고문의',
          icon: <Campaign />,
          path: '/mypage/ad-inquiry'
        }
      ]
    },
    {
      title: '설정',
      items: [
        {
          label: '알림 설정',
          icon: <Settings />,
          path: '/mypage/settings'
        }
      ]
    }
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '320px' },
          bgcolor: '#f5f5f5',
          maxWidth: '100vw'
        }
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 2 }, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '1.125rem' } }}>
            메뉴
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {menuItems.map((section, sectionIndex) => (
          <Box key={section.title}>
            <Box sx={{ px: { xs: 3, sm: 2 }, py: { xs: 2, sm: 1.5 }, bgcolor: '#f0f0f0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: { xs: '1rem', sm: '0.875rem' } }}>
                {section.title}
              </Typography>
            </Box>
            <List sx={{ py: 0 }}>
              {section.items.map((item, itemIndex) => (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    onClick={() => handleMenuClick(item.path)}
                    sx={{
                      py: { xs: 2.5, sm: 2 },
                      px: { xs: 3, sm: 2 },
                      minHeight: { xs: '64px', sm: '56px' },
                      '&:hover': {
                        bgcolor: 'primary.light',
                        '& .MuiListItemIcon-root': {
                          color: 'primary.main'
                        }
                      },
                      '&:active': {
                        bgcolor: 'primary.main',
                        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                          color: 'white'
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: { xs: 56, sm: 48 }, 
                      color: 'text.secondary',
                      fontSize: { xs: '1.5rem', sm: '1.25rem' }
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: { xs: '1.1rem', sm: '1rem' },
                        fontWeight: 500
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {sectionIndex < menuItems.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
};

export default ReviewerDrawer; 